import * as React from 'react';
import { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button } from 'antd';
import { MAPBOX_API_KEY } from '../../utils/ClientsideApiKeys';
import { debounce } from '../../utils/PerformanceUtils';

import mapboxgl from 'mapbox-gl';
import { ErrorBoundary } from '@sentry/react';
import { createRoot, Root } from 'react-dom/client';
import MapGalleryMarkerPopover, {
	MapGalleryMarkerPopoverHandle,
	MapGalleryMarkerPopupPayload,
} from '../mapboxgallery/MapGalleryMarkerPopover';
import useMountEffect from '../../hooks/useMountEffect';
import '../mapboxgallery/MapboxGallery.less';
const isEqual = require('fast-deep-equal');

const CENTER_OF_UNITED_STATES = [-98.5795, 39.8283];

/** Default max zoom for locations overview initial fit (country / multi-region, not city level). */
export const LOCATIONS_MAP_COUNTRY_LEVEL_MAX_ZOOM = 5;

export function hasMapCoordinate(value) {
	if (value === '' || value == null) {
		return false;
	}
	const numericValue = Number(value);
	return Number.isFinite(numericValue);
}

function areMapBoundsDegenerate(
	minLongitude: number,
	maxLongitude: number,
	minLatitude: number,
	maxLatitude: number
): boolean {
	const longitudeSpan = Math.abs(maxLongitude - minLongitude);
	const latitudeSpan = Math.abs(maxLatitude - minLatitude);
	return longitudeSpan < 1e-6 && latitudeSpan < 1e-6;
}

const DEFAULT_POPUP_RENDER_FUNC = (gle) =>
	`<div style="width: 25px; height: 25px; border-radius: 50%;"><img width=100%" height="auto" src="${gle.logoURL}" /></div><div>${gle.displayName}</div>`;
const DEFAULT_LONGITUDE_ACCESSOR = (el) => el.longitude;
const DEFAULT_LATITUDE_ACCESSOR = (el) => el.latitude;

const LocationsMapboxGalleryComponent: React.FC<any> = (props) => {
	const mapRef = useRef<any>(null);
	const mapContainerRef = useRef<any>(null);
	const mapResizeObserverRef = useRef<ResizeObserver | null>(null);
	const resizeFrameRef = useRef<number | null>(null);
	const markerRootsRef = useRef<Root[]>([]);
	// @ts-ignore
	const markersRef = useRef<mapboxgl.Marker[]>([]);
	const activeMarkerIdRef = useRef<number | null>(null);
	const markerPopoverHandlesRef = useRef<(MapGalleryMarkerPopoverHandle | null)[]>([]);
	const markerPopupPayloadsRef = useRef<MapGalleryMarkerPopupPayload[]>([]);
	// The geolocated entities the current markers were built for. Marker / payload indices align
	// with this array, so it backs the imperative "open this entity's popup" lookup from the list.
	const builtEntitiesRef = useRef<any[]>([]);
	// One-shot `moveend` handler queued by `showEntityPopup` when it pans an off-screen marker into
	// view. Tracked so a newer selection can cancel it — otherwise the stale handler fires on the
	// next moveend and reopens the wrong marker after quick row switches.
	const pendingPopupOpenHandlerRef = useRef<(() => void) | null>(null);
	// `bounds`/`center` back the "redo search in area" action but never drive render, so they live
	// in refs; only `showSearchWithinArea` (which toggles the button) is component state.
	const boundsRef = useRef<any>({});
	const centerRef = useRef<any>(CENTER_OF_UNITED_STATES);
	// Last geolocated entities a marker build ran for — deep-compared so we only rebuild on change
	// (the function-component equivalent of the old componentDidUpdate guard).
	const prevEntitiesRef = useRef<any>(null);

	const [showSearchWithinArea, setShowSearchWithinArea] = useState(false);

	// Keep the latest prop value available to the stable (created-once) debounced moveend callback.
	const hideSearchWithinAreaRef = useRef(props.hideSearchWithinArea);
	hideSearchWithinAreaRef.current = props.hideSearchWithinArea;

	// Imperative helpers are function declarations (hoisted, so the mutually-recursive marker
	// helpers can reference each other) and operate purely on refs + their arguments, so the
	// specific closure instance a given render produces is irrelevant.
	function updateCenter() {
		if (mapRef.current) {
			const center = mapRef.current.getCenter();
			centerRef.current = [center.lng, center.lat];
		}
	}

	function observeMapResize() {
		if (!mapContainerRef.current || typeof ResizeObserver === 'undefined') {
			return;
		}
		disconnectMapResize();
		mapResizeObserverRef.current = new ResizeObserver(() => {
			if (resizeFrameRef.current !== null || typeof window === 'undefined') {
				return;
			}
			resizeFrameRef.current = window.requestAnimationFrame(() => {
				resizeFrameRef.current = null;
				if (mapRef.current) {
					mapRef.current.resize();
				}
			});
		});
		mapResizeObserverRef.current.observe(mapContainerRef.current);
	}

	function disconnectMapResize() {
		if (resizeFrameRef.current !== null && typeof window !== 'undefined') {
			window.cancelAnimationFrame(resizeFrameRef.current);
			resizeFrameRef.current = null;
		}
		if (mapResizeObserverRef.current) {
			mapResizeObserverRef.current.disconnect();
			mapResizeObserverRef.current = null;
		}
	}

	function clearMarkersAndRoots() {
		if (markersRef.current && markersRef.current.length) {
			markersRef.current.forEach((marker) => {
				marker.remove();
			});
		}
		if (markerRootsRef.current && markerRootsRef.current.length) {
			markerRootsRef.current.forEach((root) => {
				root.unmount();
			});
		}
		markersRef.current = [];
		markerRootsRef.current = [];
		markerPopoverHandlesRef.current = [];
	}

	function handleMarkerOpenChange(markerIndex: number, open: boolean) {
		const { fetchLocationDetails } = props;

		if (!open) {
			const handle = markerPopoverHandlesRef.current[markerIndex];
			if (handle) {
				handle.cancelDetailsFetch();
			}
			activeMarkerIdRef.current = null;
			rerenderMarkerPopovers();
			return;
		}

		// Pin click (or any non-sidebar open) must supersede a sidebar pan→open still queued.
		cancelPendingPopupOpen();

		const previousMarkerIndex = activeMarkerIdRef.current;
		if (previousMarkerIndex !== null && previousMarkerIndex !== markerIndex) {
			const previousHandle = markerPopoverHandlesRef.current[previousMarkerIndex];
			if (previousHandle) {
				previousHandle.cancelDetailsFetch();
			}
		}

		activeMarkerIdRef.current = markerIndex;
		rerenderMarkerPopovers();

		if (fetchLocationDetails) {
			queueMicrotask(() => {
				const handle = markerPopoverHandlesRef.current[markerIndex];
				if (handle) {
					handle.loadDetailsIfNeeded();
				}
			});
		}
	}

	function setMarkerPopoverHandle(
		markerIndex: number,
		handle: MapGalleryMarkerPopoverHandle | null
	) {
		markerPopoverHandlesRef.current[markerIndex] = handle;
	}

	function renderMarkerPopover(markerIndex: number, entity: any, isOpen: boolean) {
		const { popupRenderFunc, fetchLocationDetails } = props;
		return (
			<MapGalleryMarkerPopover
				ref={(handle) => setMarkerPopoverHandle(markerIndex, handle)}
				popup={fetchLocationDetails ? undefined : markerPopupPayloadsRef.current[markerIndex]}
				entity={fetchLocationDetails ? entity : undefined}
				popupRenderFunc={fetchLocationDetails ? popupRenderFunc : undefined}
				onFetchDetails={fetchLocationDetails}
				getPopupContainer={() => mapContainerRef.current}
				isOpen={isOpen}
				onOpenChange={(open) => handleMarkerOpenChange(markerIndex, open)}
			/>
		);
	}

	function rerenderMarkerPopovers() {
		markerRootsRef.current.forEach((root, index) => {
			const entity = builtEntitiesRef.current[index];
			if (entity === undefined) return;
			root.render(renderMarkerPopover(index, entity, activeMarkerIdRef.current === index));
		});
	}

	// Drop any popup-open still queued from a previous off-screen selection and halt the in-flight
	// pan, so a superseded selection can neither reopen the wrong marker on the next moveend nor keep
	// drifting the map toward it.
	function cancelPendingPopupOpen() {
		if (pendingPopupOpenHandlerRef.current && mapRef.current) {
			mapRef.current.off('moveend', pendingPopupOpenHandlerRef.current);
			mapRef.current.stop();
		}
		pendingPopupOpenHandlerRef.current = null;
	}

	// Open a given entity's marker popup without changing the zoom level. If the marker is outside
	// the current viewport, pan (not zoom) until it is in view, then open the popup on `moveend`.
	// Returns false when there is no matching geolocated marker (e.g. an entity without coordinates).
	function showEntityPopup(entity) {
		if (!mapRef.current || entity == null) {
			return false;
		}
		const {
			longitudeAccessor = DEFAULT_LONGITUDE_ACCESSOR,
			latitudeAccessor = DEFAULT_LATITUDE_ACCESSOR,
		} = props;
		const entities = builtEntitiesRef.current || [];
		let markerIndex = entities.indexOf(entity);
		if (markerIndex === -1) {
			markerIndex = entities.findIndex(
				(candidate) => candidate && entity && candidate.id === entity.id
			);
		}
		if (markerIndex === -1) {
			return false;
		}
		const matchedEntity = entities[markerIndex];
		const longitude = Number(longitudeAccessor(matchedEntity));
		const latitude = Number(latitudeAccessor(matchedEntity));
		if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
			return false;
		}
		const lngLat: [number, number] = [longitude, latitude];
		const openPopup = () => handleMarkerOpenChange(markerIndex, true);
		// Supersede any popup-open still pending from an earlier off-screen selection.
		cancelPendingPopupOpen();
		if (mapRef.current.getBounds().contains(lngLat)) {
			openPopup();
		} else {
			// `panTo` keeps the current zoom and animates the center to the marker; open once settled.
			// Track the handler so a newer selection can cancel it before this pan's moveend fires.
			const handlePanEnd = () => {
				pendingPopupOpenHandlerRef.current = null;
				openPopup();
			};
			pendingPopupOpenHandlerRef.current = handlePanEnd;
			mapRef.current.once('moveend', handlePanEnd);
			mapRef.current.panTo(lngLat);
		}
		return true;
	}

	// Re-created each render so it closes over the latest accessors; `props.forwardedRef` lets the
	// fullscreen list call `showEntityPopup` imperatively when a row is clicked.
	useImperativeHandle(props.forwardedRef, () => ({ showEntityPopup }));

	function buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor) {
		if (!mapRef.current || !mapContainerRef.current) {
			return;
		}
		// Filter/entity updates replace markers by index; drop any pan→openPopup still queued from
		// a sidebar click so moveend cannot open the wrong row in the new set.
		cancelPendingPopupOpen();
		const { fetchLocationDetails } = props;
		clearMarkersAndRoots();
		activeMarkerIdRef.current = null;
		builtEntitiesRef.current = geolocatedEntities;
		markerPopupPayloadsRef.current = fetchLocationDetails
			? geolocatedEntities
			: geolocatedEntities.map((gle) => popupRenderFunc(gle));
		markersRef.current = geolocatedEntities.map((gle, index) => {
			const mountEl = document.createElement('div');
			const root = createRoot(mountEl);
			root.render(renderMarkerPopover(index, gle, false));
			markerRootsRef.current.push(root);
			const marker = new mapboxgl.Marker({
				element: mountEl,
				anchor: 'bottom',
			})
				.setLngLat([longitudeAccessor(gle), latitudeAccessor(gle)])
				.addTo(mapRef.current);
			return marker;
		});
	}

	function fitMapToEntities(
		geolocatedEntities,
		longitudeAccessor,
		latitudeAccessor,
		fitBoundsPadding = 40,
		fitBoundsMaxZoom?,
		lockBoundsAfterFit = false,
		disableDragPan = false,
		fitBoundsAnimate = true
	) {
		if (!mapRef.current) {
			return;
		}
		const filteredGeolocatedEntities = geolocatedEntities.filter((gle) => {
			const longitude = longitudeAccessor(gle);
			const latitude = latitudeAccessor(gle);
			return hasMapCoordinate(longitude) && hasMapCoordinate(latitude);
		});
		if (filteredGeolocatedEntities.length === 0) {
			return;
		}
		const resolvedMaxZoom =
			fitBoundsMaxZoom !== undefined ? fitBoundsMaxZoom : LOCATIONS_MAP_COUNTRY_LEVEL_MAX_ZOOM;
		if (lockBoundsAfterFit) {
			mapRef.current.setMaxBounds(null);
		}
		if (filteredGeolocatedEntities.length === 1) {
			const longitude = Number(longitudeAccessor(filteredGeolocatedEntities[0]));
			const latitude = Number(latitudeAccessor(filteredGeolocatedEntities[0]));
			mapRef.current.jumpTo({
				center: [longitude, latitude],
				zoom: resolvedMaxZoom,
			});
		} else {
			const minLongitude = Math.min.apply(null, filteredGeolocatedEntities.map(longitudeAccessor));
			const maxLongitude = Math.max.apply(null, filteredGeolocatedEntities.map(longitudeAccessor));
			const minLatitude = Math.min.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
			const maxLatitude = Math.max.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
			if (areMapBoundsDegenerate(minLongitude, maxLongitude, minLatitude, maxLatitude)) {
				mapRef.current.jumpTo({
					center: [minLongitude, minLatitude],
					zoom: resolvedMaxZoom,
				});
			} else {
				const fitBoundsOptions: { padding: number; maxZoom: number; animate?: boolean } = {
					padding: fitBoundsPadding,
					maxZoom: resolvedMaxZoom,
					animate: fitBoundsAnimate,
				};
				mapRef.current.fitBounds(
					[
						[minLongitude, minLatitude],
						[maxLongitude, maxLatitude],
					],
					fitBoundsOptions
				);
			}
		}
		if (lockBoundsAfterFit) {
			mapRef.current.once('moveend', () => {
				if (mapRef.current) {
					mapRef.current.setMaxBounds(mapRef.current.getBounds());
				}
			});
		}
		if (disableDragPan) {
			mapRef.current.dragPan.disable();
		}
	}

	const handleSearchWithinArea = () => {
		props.handleSearchAgain(boundsRef.current, centerRef.current);
		setShowSearchWithinArea(false);
	};

	// Created once so the debounce timer is stable across renders; reads the latest
	// `hideSearchWithinArea` via ref to mirror the class reading `this.props` at call time.
	const debouncedMoveEndCallbackRef = useRef<any>(null);
	if (debouncedMoveEndCallbackRef.current === null) {
		debouncedMoveEndCallbackRef.current = debounce(
			(bounds) => {
				if (!hideSearchWithinAreaRef.current) {
					boundsRef.current = bounds;
					setShowSearchWithinArea(true);
				}
				updateCenter();
			},
			250,
			false
		);
	}

	useMountEffect(() => {
		const {
			startingLongitude = CENTER_OF_UNITED_STATES[0],
			startingLatitude = CENTER_OF_UNITED_STATES[1],
			startingZoomLevel = 2,
			geolocatedEntities,
			popupRenderFunc = DEFAULT_POPUP_RENDER_FUNC,
			longitudeAccessor = DEFAULT_LONGITUDE_ACCESSOR,
			latitudeAccessor = DEFAULT_LATITUDE_ACCESSOR,
			showNavControls,
			navControlPosition,
			baseLayerStyle,
			renderWorldCopies = true,
			fitBoundsPadding = 40,
			fitBoundsMaxZoom,
			lockBoundsAfterFit = false,
			disableDragPan = false,
		} = props;
		// tslint:disable-next-line
		mapboxgl.accessToken = MAPBOX_API_KEY;
		mapRef.current = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: `mapbox://styles/mapbox/${baseLayerStyle}-v9`,
			center: [startingLongitude, startingLatitude],
			zoom: startingZoomLevel,
			attributionControl: false,
			renderWorldCopies,
		});
		mapRef.current.on('moveend', () => {
			debouncedMoveEndCallbackRef.current(mapRef.current.getBounds());
		});
		if (showNavControls) {
			const navigationControl = new mapboxgl.NavigationControl({ showCompass: false });
			if (navControlPosition) {
				mapRef.current.addControl(navigationControl, navControlPosition);
			} else {
				mapRef.current.addControl(navigationControl);
			}
		}

		buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor);

		fitMapToEntities(
			geolocatedEntities,
			longitudeAccessor,
			latitudeAccessor,
			fitBoundsPadding,
			fitBoundsMaxZoom,
			lockBoundsAfterFit,
			disableDragPan,
			false
		);
		observeMapResize();
		// Seed the deep-compare baseline so the update effect below doesn't rebuild on mount.
		prevEntitiesRef.current = geolocatedEntities;

		return () => {
			disconnectMapResize();
			clearMarkersAndRoots();
			if (mapRef.current) {
				mapRef.current.remove();
			}
		};
	});

	// componentDidUpdate equivalent: rebuild + refit only when geolocatedEntities deep-changes.
	// No dependency array (runs after every commit) so the deep-compare — not React's referential
	// check — decides, exactly as the class did.
	useEffect(() => {
		if (!mapRef.current) {
			return;
		}
		const {
			geolocatedEntities,
			popupRenderFunc = DEFAULT_POPUP_RENDER_FUNC,
			longitudeAccessor = DEFAULT_LONGITUDE_ACCESSOR,
			latitudeAccessor = DEFAULT_LATITUDE_ACCESSOR,
			fitBoundsPadding = 40,
			fitBoundsMaxZoom,
			lockBoundsAfterFit = false,
			disableDragPan = false,
		} = props;
		if (!isEqual(geolocatedEntities, prevEntitiesRef.current)) {
			buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor);
			fitMapToEntities(
				geolocatedEntities,
				longitudeAccessor,
				latitudeAccessor,
				fitBoundsPadding,
				fitBoundsMaxZoom,
				lockBoundsAfterFit,
				disableDragPan
			);
		}
		prevEntitiesRef.current = geolocatedEntities;
	});

	const style = {
		position: 'absolute' as 'absolute',
		top: 0,
		bottom: 0,
		left: 0,
		right: 0,
		width: '100%',
	};

	return (
		<div className="mapbox-gallery" style={style} ref={mapContainerRef}>
			{showSearchWithinArea ? (
				<Button
					style={{
						position: 'absolute',
						left: '50%',
						top: '8px',
						transform: 'translateX(-50%)',
						zIndex: 2,
					}}
					onClick={handleSearchWithinArea}
				>
					Redo search in area
				</Button>
			) : null}
		</div>
	);
};

// `ErrorBoundary` does not forward refs, so the ref is threaded to the inner component as the
// `forwardedRef` prop, where `useImperativeHandle` attaches the `showEntityPopup` method.
const LocationsMapboxGallery = React.forwardRef<any, any>((props, ref) => (
	<ErrorBoundary fallback={null}>
		<LocationsMapboxGalleryComponent {...props} forwardedRef={ref} />
	</ErrorBoundary>
));

export default LocationsMapboxGallery;
